import React from "react"
import CustomRadioGroup from "../../../../ui/atoms/RadioGroup"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
// import SocialAuth from "../SocialAuth"
import { registrationSchema } from "./schema"
import CustomTextInput from "../../../../ui/atoms/CustomTextInput"
import CustomButton from "../../../../ui/atoms/Button"
import { useAppDispatch, useAppSelector } from "../../../../state/store"
// import SeparatorWithText from "../../../../ui/atoms/SeparatorWithText"
import ErrorLabel from "../../../../ui/atoms/ErrorLabel"
import CustomAlert from "../../../../ui/atoms/Alert"
import CustomBirthdayPicker from "../../../../ui/molecules/CustomBirthdayPicker"
import CustomPasswordInput from "../../../../ui/molecules/CustomPasswordInput"
import { useAuthContents } from "../../../../contents/auth/context"
import { registerUser } from "../../state/accountRegistration/registrationActions"
import { RegistrationError } from "../../../../api/backend/backend-api"
import { login } from "../../state/login/loginActions"
import {
  buildEmailValidationBaseUrl,
  buildPasswordResetUrl,
} from "../../navigation/urls"
import CustomCheckbox from "../../../../ui/atoms/Checkbox"
import { toISODate } from "../../../../utils/dates"
import { CustomCol, CustomRow } from "../../../../ui/spacings"
import { EditorialLabel } from "../../../contents"
import Typography from "../../../../ui/atoms/Typography"
import { useLabels } from "../../../contents/hooks"
import { accountPendingPagePath } from "../../../../root/routing/urls"
import { useRouter } from "next/router"

interface RegistrationFormData {
  username: string
  password: string
  email: string
  lastName: string
  firstName: string
  birthday: string
  gender: string
  marketing: boolean
  profiling: boolean
  survey: boolean
  card: boolean
}

interface Props {
  phone?: string
  defaultFirstName?: string
  defaultLastName?: string
  redirectUrl?: string
  loginAfterRegistration?: boolean
  resetAfterRegistration?: boolean
  smColSize: number
  defaultEmail?: string
  onBack?: () => void
}

const RegisterForm = ({
  phone,
  defaultFirstName,
  defaultLastName,
  loginAfterRegistration,
  resetAfterRegistration,
  smColSize,
  defaultEmail,
  onBack,
  redirectUrl,
}: Props) => {
  const { register: contents } = useAuthContents()

  const { register, control, errors, handleSubmit, reset } = useForm({
    resolver: yupResolver(registrationSchema),
    defaultValues: {
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: defaultEmail ?? "",
      password: "",
      birthday: "",
      gender: "female",
    } as any,
  })
  const dispatch = useAppDispatch()
  const { error, isLoading } = useAppSelector((state) => state.register)

  const router = useRouter()
  const { getLabel } = useLabels()
  const { isLoading: isLoadingLogin, error: loginError } = useAppSelector(
    (state) => state.login
  )

  const fields = {
    gender: {
      name: "gender",
      label: "",
      error: errors.gender,
    },
    password: {
      name: "password",
      label: contents.form.passwordLabel,
      error: errors.password,
    },
    email: {
      name: "email",
      label: contents.form.emailLabel,
      error: errors.email,
    },
    firstName: {
      name: "firstName",
      label: contents.form.firstNameLabel,
      error: errors.firstName,
    },
    lastName: {
      name: "lastName",
      label: contents.form.lastNameLabel,
      error: errors.lastName,
    },
    birthday: {
      name: "birthday",
      label: contents.form.birthdayLabel,
      error: errors.birthday,
    },
  }

  const onRegistrationCompleted = (email: string, password: string) => {
    if (loginAfterRegistration) {
      dispatch(
        login({
          username: email,
          password,
          pwdResetBaseUrl: buildPasswordResetUrl(),
        })
      )
    }
    if (resetAfterRegistration) {
      reset()
    }
    router.push(accountPendingPagePath(email))
  }

  const onSubmit = ({
    password,
    email,
    firstName,
    lastName,
    birthday,
    gender,
    marketing,
    survey,
    card,
    profiling,
  }: RegistrationFormData) => {
    dispatch(
      registerUser(
        {
          password,
          email,
          name: firstName,
          surname: lastName,
          birthDay: toISODate(birthday as any),
          emailValidationBaseUrl: buildEmailValidationBaseUrl(),
          gender,
          profilingAccepted: profiling,
          marketingAccepted: marketing,
          surveyAccepted: survey,
          createFidelityCard: card,
          phone,
          redirectUrl,
        },
        () => onRegistrationCompleted(email, password)
      )
    )
  }

  return (
    <div>
      {/* <SocialAuth redirectUrl={redirectUrl} registration />
      <SeparatorWithText my={2}>
        {contents.form.socialAndEmailLoginSeparator}
      </SeparatorWithText> */}
      <div>
        {defaultEmail && (
          <div className="flex items-center justify-between w-full my-2">
            <Typography variant="body1">{defaultEmail}</Typography>
            <CustomButton onClick={onBack} variant="text">
              <EditorialLabel scope="common" id="edit" plainText />
            </CustomButton>
          </div>
        )}
        <Controller
          as={
            <CustomRadioGroup
              row
              options={[
                {
                  value: "female",
                  label: <EditorialLabel scope="common" id="woman" plainText />,
                },
                {
                  value: "male",
                  label: <EditorialLabel scope="common" id="man" plainText />,
                },
              ]}
            />
          }
          name="gender"
          control={control}
        />
        <CustomRow spacing className="my-6">
          <CustomCol xs={12} sm={smColSize as any}>
            <CustomTextInput
              name={fields.firstName.name}
              inputRef={register}
              label={fields.firstName.label}
              required
              fullWidth
              autoComplete="off"
              error={errors.firstName !== undefined}
            />
            <div className="my-2">
              <ErrorLabel
                error={errors.firstName !== undefined}
                message={getLabel("forms", "invalid-field")}
              />
            </div>
          </CustomCol>
          <CustomCol xs={12} sm={smColSize as any}>
            <CustomTextInput
              required
              name={fields.lastName.name}
              inputRef={register}
              label={fields.lastName.label}
              fullWidth
              autoComplete="off"
              error={errors.lastName !== undefined}
            />
            <div className="my-2">
              <ErrorLabel
                error={errors.lastName !== undefined}
                message={getLabel("forms", "invalid-field")}
              />
            </div>
          </CustomCol>
          <CustomCol xs={12} sm={smColSize as any} hidden={!!defaultEmail}>
            <CustomTextInput
              required
              name={fields.email.name}
              inputRef={register}
              label={fields.email.label}
              type="email"
              fullWidth
              autoComplete="off"
              error={errors.email !== undefined}
            />
            <div className="my-2">
              <ErrorLabel
                error={errors.email !== undefined}
                message={getLabel("forms", "invalid-field")}
              />
            </div>
          </CustomCol>
          <CustomCol xs={12} sm={smColSize as any}>
            <CustomPasswordInput
              name={fields.password.name}
              register={register}
              required
              label={fields.password.label}
              fullWidth
              error={errors.password !== undefined}
            />
            <div className="my-2">
              <ErrorLabel
                error={errors.password !== undefined}
                message={
                  errors.password?.type === "pwd-strength"
                    ? getLabel("account", "pwd-weak-error")
                    : getLabel("forms", "invalid-field")
                }
              />
            </div>
          </CustomCol>
          <CustomCol xs={12} sm={smColSize as any}>
            <Controller
              control={control}
              name="birthday"
              as={
                <CustomBirthdayPicker
                  inputName={fields.birthday.name}
                  label={fields.birthday.label}
                  fullWidth
                  autoComplete="off"
                  required
                  error={!!errors.birthday}
                />
              }
            />
            <div className="my-2">
              <ErrorLabel
                error={
                  (errors.birthday && errors.birthday.type === "age-check") ??
                  false
                }
                message={getLabel("forms", "invalid-age")}
              />
              <ErrorLabel
                error={
                  (errors.birthday && errors.birthday.type !== "age-check") ??
                  false
                }
                message={getLabel("forms", "invalid-date")}
              />
            </div>
          </CustomCol>
          {/* <CustomCol xs={12} sm={smColSize as any}>
            <div>
              <Controller
                control={control}
                name="tos"
                as={
                  <CustomCheckbox required>
                    {"Dichiaro di aver preso visione dell'"}
                    <Link href="/privacy/privacy-policy" passHref>
                      <a className="underline">informativa privacy</a>
                    </Link>{" "}
                    *
                  </CustomCheckbox>
                }
              ></Controller>
            </div>
            <div className="mt-2">
              <ErrorLabel
                error={errors.tos !== undefined}
                message={
                  "Per poter completare la registrazione devi visionare l'informativa privacy"
                }
              />
            </div>
          </CustomCol> */}
          <CustomCol xs={12} sm={smColSize as any} className="my-4">
            <Controller
              name="marketing"
              control={control}
              as={
                <CustomCheckbox>
                  <EditorialLabel scope="disclaimers" id="marketing-consent" />
                </CustomCheckbox>
              }
            ></Controller>
          </CustomCol>
          <CustomCol xs={12} sm={smColSize as any} className="my-4">
            <Controller
              name="profiling"
              control={control}
              as={
                <CustomCheckbox>
                  <EditorialLabel scope="disclaimers" id="profiling-consent" />
                </CustomCheckbox>
              }
            />
          </CustomCol>
        </CustomRow>
        <div className="my-4">
          <CustomButton
            fullWidth
            size="medium"
            onClick={handleSubmit(onSubmit)}
            loading={isLoading || isLoadingLogin}
          >
            {contents.form.registerCta}
          </CustomButton>
        </div>
        <EditorialLabel scope="account" id="registration-privacy" />
      </div>

      <CustomAlert
        severity="error"
        show={error?.type === RegistrationError.Other || loginError}
      >
        {getLabel("messages", "generic-error")}
      </CustomAlert>
      <CustomAlert
        severity="error"
        show={error?.type === RegistrationError.UserAlreadyExists}
      >
        {getLabel("account", "already-registered")}
      </CustomAlert>
    </div>
  )
}

RegisterForm.defaultProps = {
  resetAfterRegistration: true,
  loginAfterRegistration: false,
  smColSize: 12,
}

export default RegisterForm
